import { useState, useEffect, useRef, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BottomNav from '../components/BottomNav';
import OnboardingProgressHeader from '../components/OnboardingProgressHeader';
import PrimaryButton from '../components/PrimaryButton';
import sfaradImage from '../assets/webp/tradition-sfarad.webp';
import ashkenazImage from '../assets/webp/tradition-ashkenaz.webp';
import yamenImage from '../assets/webp/tradition-yamen.webp';
import moroccanImage from '../assets/webp/tradition-moroccan.webp';
import {
  trackPageView,
  trackClick,
  trackSelection,
  trackTraditionSelection,
  trackNavigation,
} from '../utils/analytics';
import { getTraditionDefaultRabbis } from '../utils/rabbiesFirestore';

const NAVY = '#13294B';

interface TraditionMeta {
  key: string;
  image: string;
  /** Accent color for the "hear sample" pill, matching the card artwork */
  accent: string;
}

// Order matters: in RTL the grid flows right-to-left (sfarad top-right, ashkenaz top-left, ...)
const traditions: TraditionMeta[] = [
  { key: 'sfarad', image: sfaradImage, accent: NAVY },
  { key: 'ashkenaz', image: ashkenazImage, accent: NAVY },
  { key: 'yamen', image: yamenImage, accent: '#8A3B24' },
  { key: 'moroccan', image: moroccanImage, accent: '#1F5E43' },
];

const getInitialTradition = (): string => {
  try {
    const saved = localStorage.getItem('selectedTradition');
    if (saved) {
      const parsed: unknown = JSON.parse(saved);
      if (
        parsed &&
        typeof parsed === 'object' &&
        typeof (parsed as { key?: unknown }).key === 'string' &&
        traditions.some((trad) => trad.key === (parsed as { key: string }).key)
      ) {
        return (parsed as { key: string }).key;
      }
    }
  } catch (e) {
    console.error('Failed to read saved tradition:', e);
  }
  return 'sfarad';
};

const TraditionPage: FC = () => {
  const { t } = useTranslation();
  const [tradition, setTraditionState] = useState<string>(getInitialTradition);
  const [lengthKey] = useState<string>('long');
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const [sampleError, setSampleError] = useState<boolean>(false);
  const [sampleUrls, setSampleUrls] = useState<Record<string, string>>({});
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    trackPageView('Tradition');
    Promise.all(
      traditions.map(async ({ key }) => {
        try {
          const defaults = await getTraditionDefaultRabbis(key);
          return { key, url: defaults.parasha?.sampleUrl ?? null };
        } catch (e) {
          console.error(`Failed to load sample URL for tradition "${key}":`, e);
          return { key, url: null };
        }
      }),
    )
      .then((results) => {
        const urls: Record<string, string> = {};
        results.forEach(({ key, url }) => {
          if (url) urls[key] = url;
        });
        setSampleUrls(urls);
      })
      .catch((e) => console.error('Failed to load tradition sample URLs:', e));
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const setTradition = (traditionData: { key: string; length: string }) => {
    setTraditionState(traditionData.key);
    localStorage.setItem('selectedTradition', JSON.stringify(traditionData));
    trackSelection('tradition', traditionData.key, {
      length: traditionData.length,
    });
  };

  const stopSample = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlayingKey(null);
  };

  const playSample = async (key: string) => {
    trackClick('Play Tradition Sample', 'Tradition', { tradition: key });
    if (playingKey === key) {
      stopSample();
      return;
    }
    stopSample();
    const url = sampleUrls[key];
    if (!url) {
      setSampleError(true);
      return;
    }
    try {
      const audio = new Audio(url);
      audio.onended = () => setPlayingKey(null);
      audioRef.current = audio;
      await audio.play();
      setSampleError(false);
      setPlayingKey(key);
    } catch (e) {
      console.error(`Failed to play tradition sample for "${key}":`, e);
      stopSample();
      setSampleError(true);
    }
  };

  const handleContinue = (): void => {
    trackClick('Continue', 'Tradition');
    setTradition({ key: tradition, length: lengthKey });
    trackTraditionSelection(tradition, lengthKey);
    // Only the tradition is persisted; the default rabbi is always resolved from the
    // rabbiTraditionDefaults map (useTraditionRabbi). Warm the cache for the next pages.
    getTraditionDefaultRabbis(tradition).catch((e) =>
      console.error(`Failed to prefetch default rabbis for tradition "${tradition}":`, e),
    );
    trackNavigation('Tradition', 'ChooseReadings', 'button');
    navigate('/choose-readings');
  };

  return (
    <div
      className="mx-auto w-full max-w-[980px] flex flex-col "
      style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
    >
      <OnboardingProgressHeader step={3} />
      <main className="flex-1 overflow-y-auto px-4 pt-4 pb-[calc(env(safe-area-inset-bottom)+90px)]">
        <h2 className="text-center text-l font-bold mb-4" style={{ color: NAVY }}>
          {t('tradition_choose_header')}
        </h2>
        <div style={{ width: '80%', margin: '0 auto' }}>
          <div className="grid grid-cols-2 gap-3">
            {traditions.map(({ key, image, accent }) => {
              const active = key === tradition;
              const isPlaying = playingKey === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTradition({ key, length: lengthKey })}
                  aria-pressed={active}
                  aria-label={t(`tradition.${key}`) || key}
                  className={
                    'm-2 relative w-full rounded-2xl bg-white flex flex-col items-center pt-2 pb-3 px-2 transition-all duration-300 focus:outline-none ' +
                    (active
                      ? 'border-2 border-[#13294B] shadow-[0_8px_24px_rgba(19,41,75,0.18)]'
                      : 'border border-transparent shadow-[0_2px_12px_rgba(19,41,75,0.08)] hover:shadow-lg')
                  }
                >
                  {active && (
                    <span
                      className="absolute flex items-center justify-center rounded-full"
                      style={{
                        top: 8,
                        insetInlineEnd: 8,
                        width: 22,
                        height: 22,
                        background: NAVY,
                        boxShadow: '0 2px 6px rgba(19,41,75,0.35)',
                      }}
                    >
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="3.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                  )}
                  <img
                    src={image}
                    alt={t(`tradition.${key}`) || key}
                    className="h-[150px] object-contain"
                    draggable={false}
                  />
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={t('tradition_listen_sample') || ''}
                    onClick={(e) => {
                      e.stopPropagation();
                      void playSample(key);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        void playSample(key);
                      }
                    }}
                    className="mt-1 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-white text-xs font-semibold cursor-pointer"
                    style={{ background: accent, position: 'absolute', bottom: '-10px' }}
                  >
                    {t('tradition_listen_sample')}
                    <span
                      className="flex items-center justify-center rounded-full"
                      style={{ width: 14, height: 14, background: '#E9C546' }}
                    >
                      {isPlaying ? (
                        <svg width="6" height="6" viewBox="0 0 8 8">
                          <rect width="8" height="8" rx="1" fill={accent} />
                        </svg>
                      ) : (
                        <svg width="7" height="7" viewBox="0 0 10 10">
                          <path d="M2 1 L9 5 L2 9 Z" fill={accent} />
                        </svg>
                      )}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {sampleError && (
          <p className="text-center text-sm mt-4" style={{ color: '#B3261E' }}>
            {t('tradition_sample_error')}
          </p>
        )}

        <p className="text-center text-base mt-2 mb-3" style={{ color: '#3F4756' }}>
          {t('tradition_change_later')}
        </p>

        <PrimaryButton
          onClick={handleContinue}
          text={t('continue')}
          variant="gold"
          style={{ fontWeight: 700, fontSize: 18 }}
        />
      </main>
      <BottomNav />
    </div>
  );
};

export default TraditionPage;
